/**
 * Color Picker for Shopify Storefront - CDN Version
 * Vanilla JS - No dependencies - Optimized for 1M+ colors
 * 
 */

(function() {
	'use strict';

	/**
	 * CONFIGURATION
	 * Replace with your config.json URL from WordPress JSON Generator
	 */
	const DEFAULT_CONFIG = {
		CDN_CONFIG_URL: 'https://uk-colourpicker.ankerstuy.com/wp-content/uploads/color-catalog-json/config.json',
		MAX_RESULTS: 40,
		DEBOUNCE_DELAY: 300,
		CACHE_DURATION: 3600000 // 1 hour in milliseconds
	};

	// Allow window.CCM_CONFIG to override defaults (for testing)
	const CONFIG = window.CCM_CONFIG ? Object.assign({}, DEFAULT_CONFIG, window.CCM_CONFIG) : DEFAULT_CONFIG;

	/**
	 * Color Picker Class - CDN Version
	 * Loads categories/colors from static JSON files
	 * Only uses REST API for search
	 */
	class ColorPickerCDN {
		constructor(config) {
			this.config = config;
			this.cdnConfig = null; // Loaded from config.json
			this.modal = null;
			this.searchInput = null;
			this.searchScopeRadios = null;
			this.categorySelect = null;
			this.resultsContainer = null;
			this.currentRequest = null;
			this.debounceTimer = null;
			this.cache = new Map();
			this.selectedColor = null;
			this.productForm = null;
			this.categories = [];
			this.popularCategoryId = null; // Store popular category ID
			// Shopify product ID – used to load product-specific color category restrictions
			this.productId = window.productData ? String(window.productData.id) : null;

			// Pagination state
			this.currentColors = []; // Store all loaded colors
			this.displayedCount = 0; // Track how many are currently displayed
			this.colorsPerPage = 40; // How many to show per load

			this.init();
		}

		/**
		 * Initialize color picker
		 */
		init() {
			// Wait for DOM to be ready
			if (document.readyState === 'loading') {
				document.addEventListener('DOMContentLoaded', () => this.setup());
			} else {
				this.setup();
			}
		}

		/**
		 * Setup color picker on product page
		 */
		async setup() {
			// Only run on product pages - match Shopify form structure
			this.productForm = document.querySelector('form.form[data-type="add-to-cart-form"]');
			
			if (!this.productForm) {
				return; // Not a product page
			}

			// Load CDN configuration
			try {
				await this.loadCDNConfig();
			} catch (error) {
				console.error('ColorPicker: Failed to load CDN config', error);
				return;
			}

			// Create modal
			this.createModal();

		// Inject "Choose Color" button
		this.injectButton();

		// Add form validation to require color selection
		this.setupFormValidation();
	}

		/**
		 * Load CDN configuration from config.json
		 */
		async loadCDNConfig() {
			const cached = this.getCache('cdn_config');
			if (cached) {
				this.cdnConfig = cached;
				return;
			}

			const response = await fetch(this.config.CDN_CONFIG_URL);
			if (!response.ok) {
				throw new Error('Failed to load CDN config');
			}

			this.cdnConfig = await response.json();
			this.setCache('cdn_config', this.cdnConfig);
		}

		/**
		 * Inject colour selector (inline text input + OR divider + modal button) into product form
		 * Position: Above buy buttons, below variant picker
		 */
		injectButton() {
			const productFormWrapper = this.productForm.closest('product-form');

			if (!productFormWrapper) {
				return;
			}

			// Outer container
			const container = document.createElement('div');
			container.className = 'ccm-color-picker-container';

			// Heading above inline input
			const inlineHeading = document.createElement('div');
			inlineHeading.className = 'ccm-inline-heading';
			inlineHeading.textContent = 'Choose your colour';

			// Inline text input
			const inlineInput = document.createElement('input');
			inlineInput.type = 'text';
			inlineInput.id = 'ccm-inline-color-input';
			inlineInput.className = 'ccm-inline-color-input';
			inlineInput.placeholder = 'Enter brand & colour name for a custom match';
			inlineInput.autocomplete = 'off';

			// OR divider
			const orDivider = document.createElement('div');
			orDivider.className = 'ccm-or-divider';
			orDivider.textContent = 'OR';

			// Browse Colours button
			const colorButton = document.createElement('button');
			colorButton.type = 'button';
			colorButton.className = 'ccm-choose-color-btn button';
			colorButton.textContent = 'Browse Colours';
			colorButton.addEventListener('click', (e) => {
				e.preventDefault();
				this.openModal();
			});

			container.appendChild(inlineHeading);
			container.appendChild(inlineInput);
			container.appendChild(orDivider);
			container.appendChild(colorButton);

			productFormWrapper.parentNode.insertBefore(container, productFormWrapper);

			// Wire up inline input events
			this.setupInlineColorInput(inlineInput);

			// Restore display state on page load
			const selectedField = this.productForm.querySelector('input[name="properties[Colour]"]');
			const brandField = this.productForm.querySelector('input[name="properties[Colour Brand]"]');
			if ((selectedField && selectedField.value) || (brandField && brandField.value)) {
				this.showSuccessMessage(null, true);
			}
			const nameOrCodeField = this.productForm.querySelector('input[name="properties[Colour Name or Code]"]');
			if (nameOrCodeField && nameOrCodeField.value) {
				inlineInput.value = nameOrCodeField.value;
			}
		}

		/**
		 * Wire up the inline colour text input.
		 * Shows/updates the selection live after 3+ characters (200 ms debounce).
		 * Clears the selection immediately when the text drops below 3 characters.
		 * Also commits on blur or Enter.
		 */
		setupInlineColorInput(inputEl) {
			let debounceTimer = null;

			const clear = () => {
				this.removeColorProperties();
				document.querySelectorAll('.ccm-success-message').forEach(m => m.remove());
				document.querySelectorAll('.ccm-color-required-error').forEach(e => e.remove());
			};

			const commit = () => {
				const text = inputEl.value.trim();
				if (text.length >= 3) {
					this.selectColor({
						id: 'inline-custom',
						name: text,
						code: '',
						rgb: { r: 128, g: 128, b: 128 },
						rgb_string: 'rgb(128,128,128)'
					});
				} else {
					clear();
				}
			};

			// Live update as the customer types
			inputEl.addEventListener('input', () => {
				clearTimeout(debounceTimer);
				const text = inputEl.value.trim();
				if (text.length >= 3) {
					debounceTimer = setTimeout(commit, 200);
				} else {
					clear();
				}
			});

			// Commit immediately on blur or Enter
			inputEl.addEventListener('blur', () => {
				clearTimeout(debounceTimer);
				commit();
			});
			inputEl.addEventListener('keydown', (e) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					clearTimeout(debounceTimer);
					commit();
					inputEl.blur();
				}
			});
		}

		/**
		 * Create modal HTML structure
		 */
		createModal() {
			const modal = document.createElement('div');
			modal.className = 'ccm-modal';
			modal.id = 'ccm-color-modal';
			
			modal.innerHTML = `
				<div class="ccm-modal-overlay"></div>
				<div class="ccm-modal-content">
					<div class="ccm-modal-header">
						<h2>Choose your colour</h2>
						<button type="button" class="ccm-modal-close" aria-label="Close">&times;</button>
					</div>
					<div class="ccm-modal-body">
					<div class="ccm-filter-section">
						<div class="ccm-filter-group">
							<label for="ccm-category-select">Choose from a colour chart</label>
							<select id="ccm-category-select" class="ccm-filter-select">
								<option value="">--Choose a colour chart</option>
							</select>
							<div class="ccm-category-text">Not sure what to choose? <a href="https://www.ankerstuyshop.co.uk/pages/contact" target="_blank">Get in touch with us</a></div>
						</div>
						<div class="ccm-filter-group">
							<label for="ccm-search-input">Search by name or colour code or tags</label>
							<input 
								type="text" 
								id="ccm-search-input" 
								class="ccm-search-input" 
								placeholder="Search colours by name, colour code, or tags..."
								autocomplete="off"
							/>
							<div class="ccm-search-scope">
								<label class="ccm-radio-label">
									<input 
										type="radio" 
										name="search-scope" 
										value="all" 
										class="ccm-radio-input"
										checked="checked"
									/>
									<span>Entire database</span>
								</label>							
								<label class="ccm-radio-label">
									<input 
										type="radio" 
										name="search-scope" 
										value="current" 
										class="ccm-radio-input"
									/>
									<span>within colour chart</span>
								</label>
							</div>
						</div>
						<div class="ccm-filter-notice">
							<p><b>Please note:</b> Colours shown may vary due to differences in screen displays. All colours are provided as a guide only, and it is the customers responsibility to ensure the correct colour is selected. Custom-mixed paint is non-returnable.</p>
						</div>
					</div>
					<div class="ccm-custom-color-notice">
						<p>Can't find your colour or brand? <button type="button" class="ccm-custom-color-link" id="ccm-custom-color-btn">Click here to enter your desired colour</button></p>
					</div>
					<div class="ccm-custom-color-form" id="ccm-custom-color-form" style="display: none;">
						<h3>Enter Custom colour</h3>
						<div class="ccm-form-group">
							<label for="ccm-custom-name">Colour Brand *</label>
							<input type="text" id="ccm-custom-name" class="ccm-form-input" placeholder="e.g RAL, NCS" required />
						</div>
						<div class="ccm-form-group">
							<label for="ccm-custom-code">Colour Name or Code *</label>
							<input type="text" id="ccm-custom-code" class="ccm-form-input" placeholder="e.g RAL 9016, NCS S 0550-N" required />
						</div>
						<div class="ccm-form-group">
							<label for="ccm-custom-rgb">RGB Values (optional)</label>
							<div class="ccm-rgb-inputs">
								<input type="number" id="ccm-custom-rgb-r" class="ccm-form-input-small" placeholder="R" min="0" max="255" />
								<input type="number" id="ccm-custom-rgb-g" class="ccm-form-input-small" placeholder="G" min="0" max="255" />
								<input type="number" id="ccm-custom-rgb-b" class="ccm-form-input-small" placeholder="B" min="0" max="255" />
							</div>
						</div>
						<div class="ccm-form-actions">
							<button type="button" class="ccm-btn-cancel" id="ccm-custom-cancel">Cancel</button>
							<button type="button" class="ccm-btn-submit" id="ccm-custom-submit">Select This Colour</button>
						</div>
					</div>
					<div class="ccm-results-container" id="ccm-results">
						<p class="ccm-help-text">Search or select a category to browse colours.</p>
					</div>
					</div>
				</div>
			`;

			document.body.appendChild(modal);

		// Store references
		this.modal = modal;
		this.searchInput = modal.querySelector('#ccm-search-input');
		this.searchScopeRadios = modal.querySelectorAll('input[name="search-scope"]');
		this.categorySelect = modal.querySelector('#ccm-category-select');
		this.resultsContainer = modal.querySelector('#ccm-results');

			// Setup event listeners
			this.setupEventListeners();
		}

		/**
		 * Setup modal event listeners
		 */
		setupEventListeners() {
			// Close button
			const closeBtn = this.modal.querySelector('.ccm-modal-close');
			closeBtn.addEventListener('click', () => this.closeModal());

			// Overlay click
			const overlay = this.modal.querySelector('.ccm-modal-overlay');
			overlay.addEventListener('click', () => this.closeModal());

			// ESC key
			document.addEventListener('keydown', (e) => {
				if (e.key === 'Escape' && this.modal.style.display === 'flex') {
					this.closeModal();
				}
			});

		// Search input
		this.searchInput.addEventListener('input', (e) => {
			this.debounceSearch(e.target.value);
		});

		// Search scope radio buttons - re-trigger search when scope changes
		this.searchScopeRadios.forEach(radio => {
			radio.addEventListener('change', () => {
				const query = this.searchInput.value.trim();
				if (query.length >= 3) {
					// Re-run the search with the new scope
					this.searchColors(query);
				}
			});
		});

	// Category/Subcategory combined select
	this.categorySelect.addEventListener('change', (e) => {
		this.handleSelectionChange(e.target.value);
	});

		// Custom color button
		const customColorBtn = this.modal.querySelector('#ccm-custom-color-btn');
		customColorBtn.addEventListener('click', () => this.showCustomColorForm());

		// Custom color form cancel
		const customCancelBtn = this.modal.querySelector('#ccm-custom-cancel');
		customCancelBtn.addEventListener('click', () => this.hideCustomColorForm());

		// Custom color form submit
		const customSubmitBtn = this.modal.querySelector('#ccm-custom-submit');
		customSubmitBtn.addEventListener('click', () => this.submitCustomColor());
	}

	/**
	 * Open modal
	 */
	async openModal() {
		this.modal.style.display = 'flex';
		//this.searchInput.focus();
		document.body.style.overflow = 'hidden';

		// Load categories if not loaded
		if (this.categories.length === 0) {
			await this.loadCategories();
		} else {
			// Categories already loaded — pick popular if available, else first category
			const popularAvailable = this.popularCategoryId &&
				this.categories.some(cat => String(cat.id) === String(this.popularCategoryId));

			if (popularAvailable) {
				await this.loadPopularCategory();
			} else if (this.categories.length > 0) {
				this.popularCategoryId = this.categories[0].id;
				await this.loadPopularCategory();
			}
		}
	}

		/**
		 * Close modal
		 */
		closeModal() {
			this.modal.style.display = 'none';
			this.searchInput.value = '';
			this.categorySelect.value = '';
			this.resultsContainer.innerHTML = '<p class="ccm-help-text">Search or select a category to browse colours.</p>';
			document.body.style.overflow = '';

			// Abort any pending request
			if (this.currentRequest) {
				this.currentRequest.abort();
				this.currentRequest = null;
			}
		}

	/**
	 * Show custom color entry form
	 */
	showCustomColorForm() {
		const form = this.modal.querySelector('#ccm-custom-color-form');
		const results = this.resultsContainer;
		
		// Hide results and show form
		results.style.display = 'none';
		form.style.display = 'block';
		
		// Clear form inputs
		this.modal.querySelector('#ccm-custom-name').value = '';
		this.modal.querySelector('#ccm-custom-code').value = '';
		const rgbRInput = this.modal.querySelector('#ccm-custom-rgb-r');
		const rgbGInput = this.modal.querySelector('#ccm-custom-rgb-g');
		const rgbBInput = this.modal.querySelector('#ccm-custom-rgb-b');
		if (rgbRInput) rgbRInput.value = '';
		if (rgbGInput) rgbGInput.value = '';
		if (rgbBInput) rgbBInput.value = '';
		
		// Focus on name input
		this.modal.querySelector('#ccm-custom-name').focus();
	}

	/**
	 * Hide custom color entry form
	 */
	hideCustomColorForm() {
		const form = this.modal.querySelector('#ccm-custom-color-form');
		const results = this.resultsContainer;
		
		// Show results and hide form
		form.style.display = 'none';
		results.style.display = 'block';
	}

	/**
	 * Submit custom color
	 */
	submitCustomColor() {
		const nameInput = this.modal.querySelector('#ccm-custom-name');
		const codeInput = this.modal.querySelector('#ccm-custom-code');
		const rgbRInput = this.modal.querySelector('#ccm-custom-rgb-r');
		const rgbGInput = this.modal.querySelector('#ccm-custom-rgb-g');
		const rgbBInput = this.modal.querySelector('#ccm-custom-rgb-b');
		
		// Validate required fields
		const name = nameInput.value.trim();
		const code = codeInput.value.trim();
		
		if (!name || !code) {
			alert('Please enter both Colour Name and Colour Code.');
			return;
		}
		
		// Get RGB values (optional) - check if inputs exist first
		const r = rgbRInput && rgbRInput.value ? parseInt(rgbRInput.value) : null;
		const g = rgbGInput && rgbGInput.value ? parseInt(rgbGInput.value) : null;
		const b = rgbBInput && rgbBInput.value ? parseInt(rgbBInput.value) : null;
		
		// Validate RGB if provided
		if ((r !== null || g !== null || b !== null) && (r === null || g === null || b === null)) {
			alert('Please enter all three RGB values (R, G, and B) or leave them all empty.');
			return;
		}
		
		// Create custom color object
		const customColor = {
			id: 'custom',
			name: name,
			code: code,
			rgb: r !== null && g !== null && b !== null ? { r, g, b } : { r: 128, g: 128, b: 128 },
			rgb_string: r !== null && g !== null && b !== null ? `rgb(${r},${g},${b})` : 'rgb(128,128,128)'
		};
		
		// Select the custom color
		this.selectColor(customColor);
	}

	/**
	 * Load categories with nested subcategories.
	 * Performs a cheap gate check (/product-has-restrictions) first when a product
	 * ID is available. If restricted, fetches /product-categories from the REST API.
	 * Otherwise loads from the static CDN file (fast path for unrestricted products).
	 */
	async loadCategories() {
		try {
			if (this.productId && this.cdnConfig && this.cdnConfig.api_base_url) {
				const gateKey = `product_has_restrictions_${this.productId}`;
				let hasRestrictions = this.getCache(gateKey);

				if (hasRestrictions === null) {
					const gateUrl = `${this.cdnConfig.api_base_url}/product-has-restrictions?product_id=${encodeURIComponent(this.productId)}`;
					const gateResponse = await fetch(gateUrl);
					if (!gateResponse.ok) throw new Error('Failed to check product restrictions');
					const gateResult = await gateResponse.json();
					hasRestrictions = (gateResult.success && gateResult.data)
						? gateResult.data.has_restrictions
						: false;
					this.setCache(gateKey, hasRestrictions);
				}

				if (hasRestrictions) {
					// Product has custom restrictions – fetch filtered category tree from REST API
					const url = `${this.cdnConfig.api_base_url}/product-categories?product_id=${encodeURIComponent(this.productId)}`;
					const response = await fetch(url);
					if (!response.ok) throw new Error('Failed to load product categories');
					const result = await response.json();
					if (result.success && result.data) {
						this.categories = result.data.categories || [];
						this.popularCategoryId = result.data.popular_category_id || null;
					}
				} else {
					// No restrictions – use static CDN file (fast path)
					const cached = this.getCache('categories_with_subcategories');
					let data;
					if (cached) {
						data = cached;
					} else {
						const url = this.cdnConfig.endpoints.categories;
						const response = await fetch(url);
						if (!response.ok) throw new Error('Failed to load categories');
						data = await response.json();
						this.setCache('categories_with_subcategories', data);
					}
					this.categories = data.categories || [];
					this.popularCategoryId = data.popular_category_id || null;
				}
			} else {
				// Default path: load from CDN static file
				const cached = this.getCache('categories_with_subcategories');
				let data;
				if (cached) {
					data = cached;
				} else {
					const url = this.cdnConfig.endpoints.categories;
					const response = await fetch(url);
					if (!response.ok) throw new Error('Failed to load categories');
					data = await response.json();
					this.setCache('categories_with_subcategories', data);
				}
				this.categories = data.categories || [];
				this.popularCategoryId = data.popular_category_id || null;
			}

			// Ensure each category has a subcategories array (even if empty)
			this.categories.forEach(cat => {
				if (!cat.subcategories) {
					cat.subcategories = [];
				}
			});

			// Populate the combined select
			this.populateCombinedSelect();

			// Load popular category if available; otherwise fall back to first category
			const popularAvailable = this.popularCategoryId &&
				this.categories.some(cat => String(cat.id) === String(this.popularCategoryId));

			if (popularAvailable) {
				await this.loadPopularCategory();
			} else if (this.categories.length > 0) {
				this.popularCategoryId = this.categories[0].id;
				await this.loadPopularCategory();
			}

		} catch (error) {
			console.error('Failed to load categories', error);
			this.showError('Failed to load categories');
		}
	}

	/**
	 * Populate combined category/subcategory dropdown
	 */
	populateCombinedSelect() {
		this.categorySelect.innerHTML = '<option value="">--Choose a colour chart</option>';
		
		this.categories.forEach(cat => {
			// Add category as regular option
			const catOption = document.createElement('option');
			catOption.value = `cat_${cat.id}`;
			catOption.textContent = cat.name;
			catOption.className = 'ccm-category-option';
			this.categorySelect.appendChild(catOption);

			// Add subcategories with indentation
			if (cat.subcategories && cat.subcategories.length > 0) {
				cat.subcategories.forEach(subcat => {
					const subcatOption = document.createElement('option');
					subcatOption.value = `subcat_${subcat.id}`;
					subcatOption.textContent = `\u00A0\u00A0\u00A0\u00A0${subcat.name}`; // Add spaces for indentation
					subcatOption.className = 'ccm-subcategory-option';
					this.categorySelect.appendChild(subcatOption);
				});
			}
		});
	}

	/**
	 * Load popular category colors automatically
	 */
	async loadPopularCategory() {
		// Set the select to popular category
		this.categorySelect.value = `cat_${this.popularCategoryId}`;
		
		// Load colors from popular category
		await this.loadColorsByCategory(this.popularCategoryId);
	}

	/**
	 * Handle selection change (category or subcategory)
	 */
	async handleSelectionChange(value) {
		// Clear search input
		this.searchInput.value = '';

		if (!value) {
			this.resultsContainer.innerHTML = '<p class="ccm-help-text">Search or select a category to browse colours.</p>';
			return;
		}

		// Check if it's a category or subcategory
		if (value.startsWith('cat_')) {
			const categoryId = value.replace('cat_', '');
			await this.loadColorsByCategory(categoryId);
		} else if (value.startsWith('subcat_')) {
			const subcategoryId = value.replace('subcat_', '');
			await this.loadColorsBySubcategory(subcategoryId);
		}
	}

		/**
		 * Load colors by category from CDN
		 */
		async loadColorsByCategory(categoryId) {
			this.showLoading();

			try {
				const cacheKey = `colors_category_${categoryId}`;
				const cached = this.getCache(cacheKey);
				let data;

				if (cached) {
					data = cached;
				} else {
					const url = this.cdnConfig.endpoints.colors.by_category.replace('{category_id}', categoryId);
					const response = await fetch(url);
					if (!response.ok) throw new Error('Failed to load colors');
					data = await response.json();
					this.setCache(cacheKey, data);
				}

				const colors = data.colors || [];
				this.renderResults(colors);

			} catch (error) {
				console.error('Failed to load colors', error);
				this.showError('Failed to load colors');
			}
		}

		/**
		 * Load colors by subcategory from CDN
		 */
		async loadColorsBySubcategory(subcategoryId) {
			this.showLoading();

			try {
				const cacheKey = `colors_subcategory_${subcategoryId}`;
				const cached = this.getCache(cacheKey);
				let data;

				if (cached) {
					data = cached;
				} else {
					const url = this.cdnConfig.endpoints.colors.by_subcategory.replace('{subcategory_id}', subcategoryId);
					const response = await fetch(url);
					if (!response.ok) throw new Error('Failed to load colors');
					data = await response.json();
					this.setCache(cacheKey, data);
				}

				const colors = data.colors || [];
				this.renderResults(colors);

			} catch (error) {
				console.error('Failed to load colors', error);
				this.showError('Failed to load colors');
			}
		}

	/**
	 * Debounce search input
	 */
	debounceSearch(query) {
		clearTimeout(this.debounceTimer);

		// Only reset category filter if searching entire database
		// const searchScope = document.querySelector('input[name="search-scope"]:checked')?.value || 'current';
		// if (query.trim().length > 0 && searchScope === 'all') {
		// 	this.categorySelect.value = '';
		// }

		if (query.trim().length === 0) {
			this.resultsContainer.innerHTML = '<p class="ccm-help-text">Search or select a category to browse colours.</p>';
			return;
		}

		// Minimum 3 characters required
		if (query.trim().length < 3) {
			this.resultsContainer.innerHTML = '<p class="ccm-help-text">Please enter at least 3 characters to search.</p>';
			return;
		}

		// Show loading immediately
		this.showLoading();

		this.debounceTimer = setTimeout(() => {
			this.searchColors(query);
		}, this.config.DEBOUNCE_DELAY);
	}

	/**
	 * Search colors via REST API
	 * Note: Search still uses REST API for dynamic querying
	 */
	async searchColors(query) {
		// Get search scope
		const searchScope = document.querySelector('input[name="search-scope"]:checked')?.value || 'all';
		
		// If searching within current selection, check if category is selected
		if (searchScope === 'current') {
			if (!this.categorySelect.value) {
				this.resultsContainer.innerHTML = '<p class="ccm-help-text">Please select a category or subcategory first, then search within it.</p>';
				return;
			}
		}
		
		// Abort previous request
		if (this.currentRequest) {
			this.currentRequest.abort();
		}

		try {
			this.currentRequest = new AbortController();
			
			// Build URL with search scope parameters
			let url = `${this.cdnConfig.api_base_url}/search?q=${encodeURIComponent(query)}&limit=${this.config.MAX_RESULTS}`;

			// Attach product ID so the server restricts search to allowed categories
			if (this.productId) {
				url += `&product_id=${encodeURIComponent(this.productId)}`;
			}

			// Add category/subcategory filter if searching within current selection
			if (searchScope === 'current' && this.categorySelect.value) {
				const selectedValue = this.categorySelect.value;
				
				if (selectedValue.startsWith('cat_')) {
					const categoryId = selectedValue.replace('cat_', '');
					url += `&category_id=${categoryId}`;
				} else if (selectedValue.startsWith('subcat_')) {
					const subcategoryId = selectedValue.replace('subcat_', '');
					url += `&subcategory_id=${subcategoryId}`;
				}
			}
			
			const response = await fetch(url, {
				signal: this.currentRequest.signal
			});

			const result = await response.json();

			if (!response.ok) {
				// Handle API error response
				const errorMessage = result.error || 'Search failed. Please try again.';
				this.showError(errorMessage);
				this.currentRequest = null;
				return;
			}
			
			if (result.success && result.data && result.data.colors) {
				this.renderResults(result.data.colors);
			} else {
				this.showError('No colours found');
			}

			this.currentRequest = null;

		} catch (error) {
			if (error.name === 'AbortError') {
				// Request was cancelled, ignore
				return;
			}
			console.error('Search error', error);
			this.showError('Search failed. Please try again.');
		}
	}

		/**
		 * Render color results with pagination support
		 * @param {Array} colors - Array of colors to display
		 * @param {boolean} append - If true, append to existing results
		 */
		renderResults(colors, append = false) {
			if (!colors || colors.length === 0) {
				this.resultsContainer.innerHTML = '<p class="ccm-no-results">No colours found.</p>';
				return;
			}

			// Store colors for pagination
			if (!append) {
				this.currentColors = colors;
				this.displayedCount = 0;
			}

			// Calculate how many colors to display
			const startIndex = this.displayedCount;
			const endIndex = Math.min(startIndex + this.colorsPerPage, this.currentColors.length);
			const colorsToShow = this.currentColors.slice(startIndex, endIndex);

			// Create or get existing grid
			let grid;
			if (append) {
				grid = this.resultsContainer.querySelector('.ccm-color-grid');
				if (!grid) {
					grid = document.createElement('div');
					grid.className = 'ccm-color-grid';
					this.resultsContainer.appendChild(grid);
				}
			} else {
				this.resultsContainer.innerHTML = '';
				grid = document.createElement('div');
				grid.className = 'ccm-color-grid';
				this.resultsContainer.appendChild(grid);
			}

			// Render colors
			colorsToShow.forEach(color => {
				const card = this.createColorCard(color);
				grid.appendChild(card);
			});

			// Update displayed count
			this.displayedCount = endIndex;

			// Add or update "Load More" button
			this.updateLoadMoreButton();
		}

		/**
		 * Update or create "Load More" button
		 */
		updateLoadMoreButton() {
			// Remove existing button or message
			const existingBtn = this.resultsContainer.querySelector('.ccm-load-more');
			const existingMsg = this.resultsContainer.querySelector('.ccm-all-loaded');
			if (existingBtn) existingBtn.remove();
			if (existingMsg) existingMsg.remove();

			// Check if there are more colors to load
			if (this.displayedCount < this.currentColors.length) {
				const remaining = this.currentColors.length - this.displayedCount;
				const loadMoreBtn = document.createElement('button');
				loadMoreBtn.type = 'button';
				loadMoreBtn.className = 'ccm-load-more';
				loadMoreBtn.textContent = `Load More (${remaining} remaining)`;
				
				loadMoreBtn.addEventListener('click', () => {
					this.loadMoreColors();
				});

				this.resultsContainer.appendChild(loadMoreBtn);
			} else if (this.currentColors.length > this.colorsPerPage) {
				// Show "all loaded" message only if we had pagination
				const message = document.createElement('p');
				message.className = 'ccm-all-loaded';
				message.textContent = `All ${this.currentColors.length} colours loaded`;
				this.resultsContainer.appendChild(message);
			}
		}

		/**
		 * Load more colors (append next batch)
		 */
		loadMoreColors() {
			this.renderResults(this.currentColors, true); // append = true
		}

		/**
		 * Create color card element
		 */
		createColorCard(color) {
			const card = document.createElement('div');
			card.className = 'ccm-color-card';
			card.setAttribute('role', 'button');
			card.setAttribute('tabindex', '0');

			const rgbString = `rgb(${color.rgb.r},${color.rgb.g},${color.rgb.b})`;
			
			// Get category or subcategory name
			let displayName = '';
			
			// Convert IDs to numbers for comparison
			const subcategoryId = parseInt(color.subcategory_id, 10);
			const categoryId = parseInt(color.category_id, 10);
			
			// Check if categories are loaded and not empty
			if (this.categories && this.categories.length > 0) {
				if (subcategoryId && subcategoryId > 0) {
					// Try to find subcategory name
					for (const cat of this.categories) {
						if (cat.subcategories && cat.subcategories.length > 0) {
							const subcat = cat.subcategories.find(sc => parseInt(sc.id, 10) === subcategoryId);
							if (subcat) {
								displayName = subcat.name;
								break;
							}
						}
					}
				}
				
				// If no subcategory found, use category name
				if (!displayName && categoryId && categoryId > 0) {
					const category = this.categories.find(cat => parseInt(cat.id, 10) === categoryId);
					if (category) {
						displayName = category.name;
					}
				}
			}

			const swatchHtml = color.image_url
				? `<img class="ccm-color-swatch" src="${this.escapeHtml(color.image_url)}" alt="${this.escapeHtml(color.name)}" style="object-fit:cover;">`
				: `<div class="ccm-color-swatch" style="background-color: ${this.escapeHtml(rgbString)};"></div>`;

			card.innerHTML = `
				${swatchHtml}
				<div class="ccm-color-info">
					<div class="ccm-color-code">${this.escapeHtml(color.code)}</div>
					<div class="ccm-color-name">${this.escapeHtml(color.name)}</div>
					<div class="ccm-color-chart">${this.escapeHtml(displayName)}</div>
					<!-- <div class="ccm-color-rgb">${this.escapeHtml(rgbString)}</div> -->

				</div>
			`;

			// Click handler
			card.addEventListener('click', () => this.selectColor(color));

			// Keyboard accessibility
			card.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					this.selectColor(color);
				}
			});

			return card;
		}

		/**
		 * Get color chart name (category or subcategory) for a color
		 * @param {Object} color - Color object
		 * @returns {string} Chart name or empty string
		 */
		getColorChartName(color) {
			let displayName = '';
			
			// Convert IDs to numbers for comparison
			const subcategoryId = parseInt(color.subcategory_id, 10);
			const categoryId = parseInt(color.category_id, 10);
			
			// Check if categories are loaded and not empty
			if (this.categories && this.categories.length > 0) {
				if (subcategoryId && subcategoryId > 0) {
					// Try to find subcategory name
					for (const cat of this.categories) {
						if (cat.subcategories && cat.subcategories.length > 0) {
							const subcat = cat.subcategories.find(sc => parseInt(sc.id, 10) === subcategoryId);
							if (subcat) {
								displayName = subcat.name;
								break;
							}
						}
					}
				}
				
				// If no subcategory found, use category name
				if (!displayName && categoryId && categoryId > 0) {
					const category = this.categories.find(cat => parseInt(cat.id, 10) === categoryId);
					if (category) {
						displayName = category.name;
					}
				}
			}
			
			return displayName;
		}

		/**
		 * Select a color and add to cart
		 */
		selectColor(color) {
			this.selectedColor = color;

			// Remove any existing error messages
			const existingErrors = document.querySelectorAll('.ccm-color-required-error');
			existingErrors.forEach(error => error.remove());

			// Remove existing color properties first to prevent duplicates
			this.removeColorProperties();

			// Check if this is a custom color
			const isCustom = color.id === 'custom';
			const isInlineCustom = color.id === 'inline-custom';

			if (isCustom) {
				// Modal custom color: use "Colour Brand" and "Colour Name or Code"
				const brandField = document.createElement('input');
				brandField.type = 'hidden';
				brandField.name = 'properties[Colour Brand]';
				// Remove newlines and trim to prevent display issues in Shopify cart
				brandField.value = String(color.name || '').replace(/[\r\n]+/g, ' ').trim();
				brandField.className = 'ccm-property';
				this.productForm.appendChild(brandField);

				const nameOrCodeField = document.createElement('input');
				nameOrCodeField.type = 'hidden';
				nameOrCodeField.name = 'properties[Colour Name or Code]';
				// Remove newlines and trim to prevent display issues in Shopify cart
				nameOrCodeField.value = String(color.code || '').replace(/[\r\n]+/g, ' ').trim();
				nameOrCodeField.className = 'ccm-property';
				this.productForm.appendChild(nameOrCodeField);

				// Add colour chart field with "Custom" value
				const chartField = document.createElement('input');
				chartField.type = 'hidden';
				chartField.name = 'properties[Colour Chart]';
				chartField.value = 'Custom';
				chartField.className = 'ccm-property';
				this.productForm.appendChild(chartField);
			} else if (isInlineCustom) {
				// Inline text input custom: Brand=Custom, Name or Code=entered text
				const brandField = document.createElement('input');
				brandField.type = 'hidden';
				brandField.name = 'properties[Colour Brand]';
				brandField.value = 'Custom';
				brandField.className = 'ccm-property';
				this.productForm.appendChild(brandField);

				const nameOrCodeField = document.createElement('input');
				nameOrCodeField.type = 'hidden';
				nameOrCodeField.name = 'properties[Colour Name or Code]';
				nameOrCodeField.value = String(color.name || '').replace(/[\r\n]+/g, ' ').trim();
				nameOrCodeField.className = 'ccm-property';
				this.productForm.appendChild(nameOrCodeField);
			} else {
				// Regular color: use existing labels
				// Get color chart name
				const chartName = this.getColorChartName(color);

				// Add color to product form as line item property
				const colorField = document.createElement('input');
				colorField.type = 'hidden';
				colorField.name = 'properties[Colour]';
				// Remove newlines and trim to prevent display issues in Shopify cart
				colorField.value = String(color.name || '').replace(/[\r\n]+/g, ' ').trim();
				colorField.className = 'ccm-property';
				this.productForm.appendChild(colorField);

				// Add color code field for reference
				const codeField = document.createElement('input');
				codeField.type = 'hidden';
				codeField.name = 'properties[Colour Code]';
				// Remove newlines and trim to prevent display issues in Shopify cart
				codeField.value = String(color.code || '').replace(/[\r\n]+/g, ' ').trim();
				codeField.className = 'ccm-property';
				this.productForm.appendChild(codeField);

				// Add color chart field if available
				if (chartName) {
					const chartField = document.createElement('input');
					chartField.type = 'hidden';
					chartField.name = 'properties[Colour Chart]';
					// Remove newlines and trim to prevent display issues in Shopify cart
					chartField.value = String(chartName || '').replace(/[\r\n]+/g, ' ').trim();
					chartField.className = 'ccm-property';
					this.productForm.appendChild(chartField);
				}
			}

			// When a catalog or modal-custom colour is picked, clear the inline text input
			if (!isInlineCustom) {
				const inlineInput = document.getElementById('ccm-inline-color-input');
				if (inlineInput) inlineInput.value = '';
			}

			// Show success message
			this.showSuccessMessage(color);

			// Close modal
			this.closeModal();
		}

		/**
		 * Remove existing color properties
		 */
		removeColorProperties() {
			// Remove all existing color-related inputs (both regular and custom)
			const existingColorFields = this.productForm.querySelectorAll(
				'input[name="properties[Colour]"], ' +
				'input[name="properties[Colour Code]"], ' +
				'input[name="properties[Colour Chart]"], ' +
				'input[name="properties[Colour Brand]"], ' +
				'input[name="properties[Colour Name or Code]"]'
			);
			existingColorFields.forEach(field => field.remove());
			
			// Also remove any fields with the ccm-property class (for consistency)
			const existingProps = this.productForm.querySelectorAll('.ccm-property');
			existingProps.forEach(prop => prop.remove());
		}

		/**
		 * Setup form validation to require color selection
		 */
		setupFormValidation() {
			// Intercept form submission
			this.productForm.addEventListener('submit', (e) => {
				if (!this.hasColorSelected()) {
					e.preventDefault();
					e.stopPropagation();
					this.showColorRequiredError();
					return false;
				}
			});

			// Also intercept buy buttons (Shopify sometimes uses separate buttons)
			const buyButtons = this.productForm.querySelectorAll('button[type="submit"], button[name="add"], .product-form__cart-submit, [data-add-to-cart]');
			buyButtons.forEach(button => {
				button.addEventListener('click', (e) => {
					if (!this.hasColorSelected()) {
						e.preventDefault();
						e.stopPropagation();
						this.showColorRequiredError();
						return false;
					}
				});
			});
		}

		/**
		 * Check if a color has been selected
		 */
		hasColorSelected() {
			const colorField = this.productForm.querySelector('input[name="properties[Colour]"]');
			const customBrandField = this.productForm.querySelector('input[name="properties[Colour Brand]"]');
			return (colorField && colorField.value.trim().length > 0) || 
			       (customBrandField && customBrandField.value.trim().length > 0);
		}

		/**
		 * Show error message when color is required
		 */
		showColorRequiredError() {
			// Remove existing error messages
			const existingErrors = document.querySelectorAll('.ccm-color-required-error');
			existingErrors.forEach(error => error.remove());

			// Create error message
			const errorMessage = document.createElement('div');
			errorMessage.className = 'ccm-color-required-error';
			errorMessage.innerHTML = `
				<div class="ccm-error-content">
					<strong>⚠️ Colour Selection Required</strong>
					<p>Please select a colour before adding this item to your cart.</p>
				</div>
			`;

			// Insert after the color button or before the form
			const colorButton = document.querySelector('.ccm-choose-color-btn');
			if (colorButton && colorButton.parentNode) {
				colorButton.parentNode.insertBefore(errorMessage, colorButton.nextSibling);
			} else if (this.productForm.parentNode) {
				this.productForm.parentNode.insertBefore(errorMessage, this.productForm);
			}

			// Scroll to error message
			errorMessage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

			// Remove error after 10 seconds
			setTimeout(() => {
				errorMessage.remove();
			}, 10000);
		}

		/**
		 * Show success message after color selection
		 */
		showSuccessMessage(color, onLoad = false) {
			// Remove ALL existing success messages (they're outside the form, so search document-wide)
			const existingMessages = document.querySelectorAll('.ccm-success-message');
			existingMessages.forEach(msg => msg.remove());

			// If onLoad, get color from hidden field
			if (onLoad) {
				const colorField = this.productForm.querySelector('input[name="properties[Colour]"]');
				if (!colorField || !colorField.value) return;
			}

			const message = document.createElement('div');
			message.className = 'ccm-success-message';
			
			const displayColor = color || { name: 'Selected colour' };
			const rgbString = color ? (color.rgb_string || `rgb(${color.rgb.r},${color.rgb.g},${color.rgb.b})`) : '#ccc';
			const isModalCustom = color && color.id === 'custom';
			const isInlineCustom = color && color.id === 'inline-custom';
			const isCustom = isModalCustom || isInlineCustom;


			const customLabel = isCustom ? '<span class="ccm-custom-label">Custom Colour</span>' : '';

			let successSwatchHtml;
			if ( isModalCustom ) {
				successSwatchHtml = `<div class="ccm-success-swatch ccm-swatch-hidden" style="background-color: ${this.escapeHtml(rgbString)};"></div>`;
			} else if ( isInlineCustom ) {
				successSwatchHtml = `<div class="ccm-success-swatch ccm-inline-swatch"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="46" height="46" aria-hidden="true"><rect x="15" y="18" width="14" height="70" rx="6" ry="6" fill="#3d5a7a" transform="rotate(63,22,88)"/><rect x="15" y="18" width="14" height="70" rx="6" ry="6" fill="#a8b4e8" transform="rotate(47,22,88)"/><rect x="15" y="18" width="14" height="70" rx="6" ry="6" fill="#f5c442" transform="rotate(30,22,88)"/><rect x="15" y="18" width="14" height="70" rx="6" ry="6" fill="#a8d8f0" transform="rotate(14,22,88)"/><rect x="15" y="18" width="14" height="70" rx="6" ry="6" fill="#e8336d" transform="rotate(-5,22,88)"/><rect x="8" y="78" width="28" height="16" rx="5" ry="5" fill="#f2f0f5"/><circle cx="22" cy="86" r="4" fill="#3d5a7a"/></svg></div>`;
			} else if ( color && color.image_url ) {
				successSwatchHtml = `<img class="ccm-success-swatch" src="${this.escapeHtml(color.image_url)}" alt="${this.escapeHtml(color.name || '')}" style="object-fit:cover;">`;
			} else {
				successSwatchHtml = `<div class="ccm-success-swatch" style="background-color: ${this.escapeHtml(rgbString)};"></div>`;
			}

			message.innerHTML = `
				<div class="ccm-success-content">
					${successSwatchHtml}
					<div class="ccm-success-text">
						<strong>Colour Selected!</strong>
						${displayColor.name}
						${customLabel}
					</div>
				</div>
			`;

			// Insert after the color button
			const colorButton = document.querySelector('.ccm-choose-color-btn');
			if (colorButton && colorButton.parentNode) {
				colorButton.parentNode.insertBefore(message, colorButton.nextSibling);
			}
		}

		/**
		 * Show loading state
		 */
		showLoading() {
			this.resultsContainer.innerHTML = '<div class="ccm-loading">Loading colours...</div>';
		}

		/**
		 * Show error message
		 */
		showError(message) {
			this.resultsContainer.innerHTML = `<div class="ccm-error">${this.escapeHtml(message)}</div>`;
		}

		/**
		 * Cache management
		 */
		getCache(key) {
			const cached = this.cache.get(key);
			if (!cached) return null;
			
			const now = Date.now();
			if (now - cached.timestamp > this.config.CACHE_DURATION) {
				this.cache.delete(key);
				return null;
			}
			
			return cached.data;
		}

		setCache(key, data) {
			this.cache.set(key, {
				data: data,
				timestamp: Date.now()
			});

			// Limit cache size
			if (this.cache.size > 50) {
				const firstKey = this.cache.keys().next().value;
				this.cache.delete(firstKey);
			}
		}

		/**
		 * Escape HTML to prevent XSS
		 */
		escapeHtml(text) {
			const div = document.createElement('div');
			div.textContent = text;
			return div.innerHTML;
		}
	}

	// Initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => {
			new ColorPickerCDN(CONFIG);
		});
	} else {
		new ColorPickerCDN(CONFIG);
	}

})();

